import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CrewPilotModuleShell } from "./CrewPilotModuleShell";
import { HubPrimaryAction } from "./shared/HubPrimaryAction";
import { PAGE_LAYOUT } from "../layout/pageLayout";
import { InvoiceReviewScreen } from "./InvoiceReviewScreen";
import { InvoiceSummaryScreen } from "./InvoiceSummaryScreen";
import { InvoiceReportPreviewScreen } from "./invoice/report/InvoiceReportPreviewScreen";
import { InvoiceReportOptionsScreen } from "./invoice/report/InvoiceReportOptionsScreen";
import { BatchSelectionToolbar } from "./shared/BatchSelectionToolbar";
import {
  DEFAULT_INVOICE_REPORT_OPTIONS,
  billingPeriodOptionsFromInvoice,
} from "../helpers/invoiceReport";
import {
  INVOICE_HUB_BATCH_ACTIONS,
  getInvoiceHubBatchMode,
} from "../helpers/invoiceHubBatchSelection";
import { findHubBatchAction } from "../helpers/hubBatchSelection";
import { useBatchSelection } from "../hooks/useBatchSelection";
import { ViewportPortal } from "./shared/ViewportDialog";
import {
  CP_MODAL_OVERLAY_STYLE,
  CP_MODAL_PANEL_STYLE,
} from "./shared/crewpilotModalStyles";
import {
  HUB_STATUS_PILL_ACTIVE_GOLD_STYLE,
  HUB_STATUS_PILL_BTN_BASE_STYLE,
  HUB_STATUS_PILLS_WRAP_STYLE,
} from "./shared/hubFilterPillStyles";
import {
  INVOICE_HUB_FILTER_CHIPS,
  invoiceMatchesHubFilter,
  resolveInvoiceHubFilter,
} from "../helpers/invoiceHubFilters";
import {
  applyInvoiceBillingPeriod,
  archiveInvoice,
  createStandaloneInvoice,
  deleteDraftInvoice,
  loadInvoiceWithLineItems,
  loadInvoices,
  recordInvoicePayment,
  markInvoiceSent,
  reopenInvoiceToDraft,
  resolveInvoiceStatus,
  reverseInvoicePayment,
  updateDraftInvoiceBillingPeriod,
  updateInvoiceDraft,
} from "../helpers/invoices";
import { formatSupabaseError, loadClientsForEstimates } from "../helpers/estimates";
import { resolveInvoiceHubView } from "../helpers/invoiceWorkflow";
import { dispatchFinancePositionChanged } from "../helpers/financeStateEvents";
import { usePermissions } from "../hooks/usePermissions";
import { CAPABILITY } from "../helpers/permissions";
import { shouldSkipSupabase } from "../helpers/guestMode";
import { useHardwareBackHandler } from "../navigation/HardwareBackContext";
import { InvoiceHubDashboard } from "./invoice/InvoiceHubDashboard";
import {
  buildAccountsReceivableDashboard,
  loadReceivableInvoices,
} from "../helpers/accountsReceivable";
import { CPInput } from "./CPInput";
import { CPDropdown } from "./CPDropdown";
import { useIsDesktopViewport } from "../hooks/useIsDesktopViewport";
import { InvoiceHubDesktop } from "./invoice/InvoiceHubDesktop";
import { InvoiceListCard } from "./invoice/InvoiceListCard";
import "./InvoiceHubScreen.css";

const CARD_STYLE = {
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(201,168,76,0.25)",
  borderRadius: "14px",
  padding: "18px 20px",
  marginBottom: "14px",
  cursor: "pointer",
};

const SEARCH_INPUT_STYLE = {
  width: "100%",
  padding: "14px 16px",
  borderRadius: "14px",
  border: "1px solid var(--nx-border, #e2e5eb)",
  background: "var(--nx-surface, #fff)",
  color: "var(--nx-text, #1a2233)",
  fontSize: "16px",
  boxSizing: "border-box",
};

const STATUS_PILLS_WRAP_STYLE = HUB_STATUS_PILLS_WRAP_STYLE;
const STATUS_PILL_BTN_BASE_STYLE = HUB_STATUS_PILL_BTN_BASE_STYLE;
const STATUS_PILL_ACTIVE_GOLD_STYLE = HUB_STATUS_PILL_ACTIVE_GOLD_STYLE;

const MODAL_OVERLAY_STYLE = CP_MODAL_OVERLAY_STYLE;
const MODAL_PANEL_STYLE = CP_MODAL_PANEL_STYLE;

const getInvoiceItemId = (invoice) => invoice.invoice_id;

function invoiceMatchesSearch(invoice, search) {
  if (!search) return true;

  return (
    invoice.title?.toLowerCase().includes(search) ||
    invoice.invoice_number?.toLowerCase().includes(search) ||
    invoice.clients?.client_name?.toLowerCase().includes(search) ||
    invoice.projects?.project_name?.toLowerCase().includes(search)
  );
}

export function InvoiceHubScreen({
  onBack,
  currentUser,
  editInvoiceId,
  initialStatusFilter = null,
  initialView = "hub",
}) {
  const [view, setView] = useState(() => (editInvoiceId ? "boot" : "hub"));
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(() =>
    resolveInvoiceHubFilter(initialStatusFilter),
  );
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeInvoiceId, setActiveInvoiceId] = useState(editInvoiceId || "");
  const [activeInvoice, setActiveInvoice] = useState(null);
  const [hubError, setHubError] = useState("");
  const [batchDeleting, setBatchDeleting] = useState(false);
  const [batchDeleteConfirmIds, setBatchDeleteConfirmIds] = useState(null);
  const [reportOptions, setReportOptions] = useState(
    DEFAULT_INVOICE_REPORT_OPTIONS,
  );
  const isDesktop = useIsDesktopViewport();
  const { can } = usePermissions();
  const canReviewInvoice = can(CAPABILITY.INVOICE_REVIEW);
  const canSendInvoice = can(CAPABILITY.INVOICE_SEND);
  const canMarkPaid = can(CAPABILITY.INVOICE_MARK_PAID);
  const canViewAccountsReceivable = can(CAPABILITY.ACCOUNTS_RECEIVABLE_VIEW);
  const [arMetrics, setArMetrics] = useState(null);
  const [arLoading, setArLoading] = useState(false);
  const [createClientId, setCreateClientId] = useState("");
  const [createTitle, setCreateTitle] = useState("");
  const [createClients, setCreateClients] = useState([]);
  const [createClientsLoading, setCreateClientsLoading] = useState(false);

  const batchMode = getInvoiceHubBatchMode(statusFilter);
  const batchSelectionEnabled = Boolean(batchMode) && canReviewInvoice;

  const reloadHub = useCallback(async () => {
    const rows = await loadInvoices(currentUser);
    setInvoices(rows);
  }, [currentUser]);

  const reloadArDashboard = useCallback(async () => {
    if (!canViewAccountsReceivable || shouldSkipSupabase(currentUser)) {
      setArMetrics(null);
      return;
    }

    setArLoading(true);
    try {
      const rows = await loadReceivableInvoices(currentUser);
      setArMetrics(buildAccountsReceivableDashboard(rows));
    } catch (error) {
      console.warn("Invoice hub AR dashboard unavailable:", error);
      setArMetrics(null);
    } finally {
      setArLoading(false);
    }
  }, [canViewAccountsReceivable, currentUser]);

  const reloadActiveInvoice = useCallback(async (invoiceId) => {
    if (!invoiceId) {
      setActiveInvoice(null);
      return null;
    }

    const invoice = await loadInvoiceWithLineItems(currentUser, invoiceId);
    setActiveInvoice(invoice);
    return invoice;
  }, [currentUser]);

  useEffect(() => {
    setStatusFilter(resolveInvoiceHubFilter(initialStatusFilter));
  }, [initialStatusFilter]);

  useEffect(() => {
    let cancelled = false;

    async function boot() {
      setLoading(true);
      setHubError("");
      if (shouldSkipSupabase(currentUser)) {
        setInvoices([]);
        setLoading(false);
        return;
      }

      try {
        await reloadHub();
        await reloadArDashboard();
        if (editInvoiceId && !cancelled) {
          setActiveInvoiceId(editInvoiceId);
          const invoice = await reloadActiveInvoice(editInvoiceId);
          setView(resolveInvoiceHubView(invoice?.status));
        }
      } catch (error) {
        if (!cancelled) {
          setHubError(formatSupabaseError(error));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    boot();
    return () => {
      cancelled = true;
    };
  }, [editInvoiceId, reloadActiveInvoice, reloadHub]);

  const filteredInvoices = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return invoices.filter(
      (invoice) =>
        invoiceMatchesHubFilter(invoice, statusFilter) &&
        invoiceMatchesSearch(invoice, search),
    );
  }, [invoices, searchTerm, statusFilter]);

  const batchSelection = useBatchSelection(filteredInvoices, getInvoiceItemId, {
    enabled: batchSelectionEnabled,
  });

  async function openInvoice(invoice) {
    setActiveInvoiceId(invoice.invoice_id);
    setHubError("");
    try {
      const loaded = await reloadActiveInvoice(invoice.invoice_id);
      setView(resolveInvoiceHubView(loaded?.status || invoice.status));
    } catch (error) {
      setHubError(formatSupabaseError(error));
    }
  }

  async function handleSaveDraft(payload) {
    if (!activeInvoice || !canReviewInvoice) return;
    setSaving(true);
    setHubError("");
    try {
      await updateInvoiceDraft(
        currentUser,
        activeInvoice.invoice_id,
        {
          ...payload,
          userId: currentUser?.id,
        },
        activeInvoice,
      );
      await reloadActiveInvoice(activeInvoice.invoice_id);
      await reloadHub();
    } catch (error) {
      setHubError(formatSupabaseError(error));
      throw error;
    } finally {
      setSaving(false);
    }
  }

  async function handleReseedBillingPeriod() {
    if (!activeInvoice) return;
    setSaving(true);
    setHubError("");
    try {
      await applyInvoiceBillingPeriod(
        currentUser,
        activeInvoice.invoice_id,
        currentUser?.id,
        billingPeriodOptionsFromInvoice(activeInvoice),
      );
      await reloadActiveInvoice(activeInvoice.invoice_id);
      await reloadHub();
    } catch (error) {
      setHubError(formatSupabaseError(error));
    } finally {
      setSaving(false);
    }
  }

  async function handleMarkSent() {
    if (!activeInvoice || !canSendInvoice) return;
    setSaving(true);
    setHubError("");
    try {
      await markInvoiceSent(currentUser, activeInvoice.invoice_id, currentUser?.id);
      const invoice = await reloadActiveInvoice(activeInvoice.invoice_id);
      await reloadHub();
      // Sent invoices are managed on Summary, not the editor.
      setView(resolveInvoiceHubView(invoice?.status || "sent"));
    } catch (error) {
      setHubError(formatSupabaseError(error));
    } finally {
      setSaving(false);
    }
  }

  async function handleReopenToDraft() {
    if (!activeInvoice || !canSendInvoice) return;
    setSaving(true);
    setHubError("");
    try {
      await reopenInvoiceToDraft(currentUser, activeInvoice.invoice_id);
      await reloadActiveInvoice(activeInvoice.invoice_id);
      await reloadHub();
      // Reopened drafts always return to the Review editor.
      setView("review");
    } catch (error) {
      setHubError(formatSupabaseError(error));
      throw error;
    } finally {
      setSaving(false);
    }
  }

  async function handleMarkPaid(paymentDetails) {
    if (!activeInvoice) return;
    if (!canMarkPaid) {
      const denied = new Error("You do not have permission to record invoice payments.");
      setHubError(denied.message);
      throw denied;
    }
    setSaving(true);
    setHubError("");
    try {
      await recordInvoicePayment(
        currentUser,
        activeInvoice.invoice_id,
        currentUser?.id,
        paymentDetails,
      );
      await reloadActiveInvoice(activeInvoice.invoice_id);
      await reloadHub();
      await reloadArDashboard();
      dispatchFinancePositionChanged({
        reason: "invoice_payment_recorded",
        invoiceId: activeInvoice.invoice_id,
      });
    } catch (error) {
      setHubError(formatSupabaseError(error));
      throw error;
    } finally {
      setSaving(false);
    }
  }

  async function handleReversePayment(paymentId) {
    if (!activeInvoice) return;
    if (!canMarkPaid) {
      const denied = new Error("You do not have permission to reverse invoice payments.");
      setHubError(denied.message);
      throw denied;
    }
    setSaving(true);
    setHubError("");
    try {
      await reverseInvoicePayment(
        currentUser,
        activeInvoice.invoice_id,
        currentUser?.id,
        paymentId,
      );
      await reloadActiveInvoice(activeInvoice.invoice_id);
      await reloadHub();
      await reloadArDashboard();
      dispatchFinancePositionChanged({
        reason: "invoice_payment_reversed",
        invoiceId: activeInvoice.invoice_id,
      });
    } catch (error) {
      setHubError(formatSupabaseError(error));
      throw error;
    } finally {
      setSaving(false);
    }
  }

  async function handleArchive(invoiceId) {
    if (!canReviewInvoice) return;

    setHubError("");
    try {
      await archiveInvoice(currentUser, invoiceId, currentUser?.id);
      await reloadHub();
      if (activeInvoiceId === invoiceId) {
        await reloadActiveInvoice(invoiceId);
      }
      if (view === "summary" && activeInvoiceId === invoiceId) {
        setView("hub");
        setActiveInvoiceId("");
        setActiveInvoice(null);
      }
    } catch (error) {
      setHubError(formatSupabaseError(error));
    }
  }

  async function handleDeleteDraft() {
    if (!activeInvoice || !canReviewInvoice) return;
    if (resolveInvoiceStatus(activeInvoice.status) !== "draft") return;

    setHubError("");
    try {
      await deleteDraftInvoice(
        currentUser,
        activeInvoice.invoice_id,
        currentUser?.id,
      );
      await reloadHub();
      setView("hub");
      setActiveInvoiceId("");
      setActiveInvoice(null);
    } catch (error) {
      setHubError(formatSupabaseError(error));
      throw error;
    }
  }

  function handleBatchAction(actionId) {
    if (!batchMode || !canReviewInvoice) return;

    const action = findHubBatchAction(batchMode, actionId);
    if (!action) return;

    const selectedIds = batchSelection.getSelectedIds();
    if (selectedIds.length === 0) return;

    if (actionId === INVOICE_HUB_BATCH_ACTIONS.DELETE_DRAFTS) {
      setBatchDeleteConfirmIds(selectedIds);
    }
  }

  async function handleConfirmBatchDelete() {
    if (!batchDeleteConfirmIds?.length || !canReviewInvoice) return;

    setBatchDeleting(true);
    setHubError("");
    try {
      for (const invoiceId of batchDeleteConfirmIds) {
        await deleteDraftInvoice(currentUser, invoiceId, currentUser?.id);
      }
      batchSelection.clearSelection();
      setBatchDeleteConfirmIds(null);
      await reloadHub();
    } catch (error) {
      setHubError(formatSupabaseError(error));
    } finally {
      setBatchDeleting(false);
    }
  }

  function openReportOptions() {
    setReportOptions((prev) => ({
      ...prev,
      ...billingPeriodOptionsFromInvoice(activeInvoice),
    }));
    setView("report-options");
  }

  async function beginCreateInvoice() {
    if (!canReviewInvoice) {
      setHubError("You do not have permission to create invoices.");
      return;
    }

    setHubError("");
    setCreateClientId("");
    setCreateTitle("");
    setView("create");
    setCreateClientsLoading(true);
    try {
      const rows = await loadClientsForEstimates(currentUser);
      setCreateClients(rows);
    } catch (error) {
      setHubError(formatSupabaseError(error));
      setCreateClients([]);
    } finally {
      setCreateClientsLoading(false);
    }
  }

  async function handleCreateInvoice() {
    if (!canReviewInvoice) {
      setHubError("You do not have permission to create invoices.");
      return;
    }
    if (!createClientId) {
      setHubError("Select a client.");
      return;
    }

    setSaving(true);
    setHubError("");
    try {
      const invoice = await createStandaloneInvoice(currentUser, {
        clientId: createClientId,
        title: createTitle,
        userId: currentUser?.id || currentUser?.supabase_user_id,
      });
      setActiveInvoiceId(invoice.invoice_id);
      setActiveInvoice(invoice);
      setView("review");
      await reloadHub();
    } catch (error) {
      setHubError(formatSupabaseError(error));
    } finally {
      setSaving(false);
    }
  }

  const createLaunchRef = useRef(false);
  useEffect(() => {
    if (createLaunchRef.current) return;
    if (initialView !== "create" || editInvoiceId || !canReviewInvoice) return;
    createLaunchRef.current = true;
    beginCreateInvoice();
  }, [initialView, editInvoiceId, canReviewInvoice]);

  const handleHardwareBack = useCallback(() => {
    if (view === "report-preview") {
      setView("report-options");
      return true;
    }

    if (view === "report-options") {
      setView(resolveInvoiceHubView(activeInvoice?.status));
      return true;
    }

    if (view === "create") {
      setView("hub");
      return true;
    }

    if (view === "review" || view === "summary") {
      setView("hub");
      setActiveInvoiceId("");
      setActiveInvoice(null);
      return true;
    }

    if (typeof onBack === "function") {
      onBack();
      return true;
    }
    return false;
  }, [activeInvoice, onBack, view]);

  useHardwareBackHandler(handleHardwareBack);

  if (view === "create") {
    return (
      <CrewPilotModuleShell
        title="Create Invoice"
        subtitle="Standalone invoice — no job required"
        sectionLabel="INVOICES"
        onBack={() => setView("hub")}
        layout={PAGE_LAYOUT.FORM}
      >
        <div style={CARD_STYLE}>
          <p style={{ opacity: 0.75, fontSize: "14px", marginTop: 0 }}>
            Create a draft invoice for a client without linking a project. Add
            line items in the invoice editor.
          </p>

          {createClientsLoading ? (
            <p style={{ opacity: 0.7 }}>Loading clients…</p>
          ) : (
            <>
              <div style={{ marginBottom: "14px" }}>
                <CPDropdown
                  label="Client"
                  value={createClientId}
                  onChange={setCreateClientId}
                  options={createClients.map((client) => ({
                    value: client.client_id,
                    label: client.client_name,
                  }))}
                  placeholder="Select a client…"
                  disabled={saving}
                />
              </div>

              <CPInput
                label="Invoice title"
                value={createTitle}
                onChange={setCreateTitle}
                placeholder="e.g. Website redesign deposit"
                disabled={saving}
              />

              {hubError ? (
                <p style={{ color: "#e06b5a", marginTop: "12px" }}>{hubError}</p>
              ) : null}

              <button
                type="button"
                className="nx-btn nx-btn-gold"
                style={{ width: "100%", marginTop: "18px" }}
                disabled={saving || !createClientId}
                onClick={handleCreateInvoice}
              >
                {saving ? "Creating…" : "Create Draft Invoice"}
              </button>
            </>
          )}
        </div>
      </CrewPilotModuleShell>
    );
  }

  if (view === "review") {
    return (
      <>
        <InvoiceReviewScreen
          invoice={activeInvoice}
          saving={saving}
          onBack={() => {
            setView("hub");
            setActiveInvoiceId("");
            setActiveInvoice(null);
          }}
          onSaveDraft={handleSaveDraft}
          onReseedBillingPeriod={handleReseedBillingPeriod}
          onPreviewInvoice={openReportOptions}
          onMarkSent={handleMarkSent}
          onDeleteDraft={handleDeleteDraft}
        />
        {hubError && (
          <div style={{ padding: "0 20px 20px", color: "#e06b5a" }}>{hubError}</div>
        )}
      </>
    );
  }

  if (view === "summary") {
    return (
      <>
        <InvoiceSummaryScreen
          invoice={activeInvoice}
          saving={saving}
          errorMessage={hubError}
          onBack={() => {
            setView("hub");
            setActiveInvoiceId("");
            setActiveInvoice(null);
          }}
          onSaveDraft={handleSaveDraft}
          onPreviewInvoice={openReportOptions}
          onExportPdf={openReportOptions}
          onMarkSent={handleMarkSent}
          onMarkPaid={handleMarkPaid}
          onReversePayment={handleReversePayment}
          onReopenToDraft={handleReopenToDraft}
          onArchiveInvoice={() => handleArchive(activeInvoice.invoice_id)}
        />
      </>
    );
  }

  if (view === "report-options") {
    return (
      <>
        <InvoiceReportOptionsScreen
          invoiceNumber={activeInvoice?.invoice_number}
          invoiceTitle={activeInvoice?.title}
          invoiceStatus={activeInvoice?.status}
          lineItems={activeInvoice?.lineItems || []}
          options={reportOptions}
          onChange={setReportOptions}
          onPreview={async (nextOptions) => {
            setHubError("");
            setSaving(true);
            try {
              // Preview must not reseed labour/materials. Refresh from Project
              // is the only path that re-imports operational lines.
              if (resolveInvoiceStatus(activeInvoice?.status) === "draft") {
                await updateDraftInvoiceBillingPeriod(
                  currentUser,
                  activeInvoiceId,
                  nextOptions,
                  currentUser?.id,
                );
                await reloadActiveInvoice(activeInvoiceId);
              }
              setReportOptions(nextOptions);
              setView("report-preview");
            } catch (error) {
              setHubError(formatSupabaseError(error));
            } finally {
              setSaving(false);
            }
          }}
          onBack={() => setView(resolveInvoiceHubView(activeInvoice?.status))}
        />
        {hubError && (
          <div style={{ padding: "0 20px 20px", color: "#e06b5a" }}>{hubError}</div>
        )}
      </>
    );
  }

  if (view === "report-preview") {
    return (
      <InvoiceReportPreviewScreen
        invoice={activeInvoice}
        options={reportOptions}
        onBack={() => setView("report-options")}
        onEditOptions={() => setView("report-options")}
      />
    );
  }

  const batchDeleteModal = batchDeleteConfirmIds ? (
    <ViewportPortal>
    <div style={MODAL_OVERLAY_STYLE}>
      <div style={MODAL_PANEL_STYLE}>
        <h3 style={{ marginTop: 0 }}>Delete Selected Drafts?</h3>
        <p style={{ opacity: 0.8, lineHeight: 1.5 }}>
          {batchDeleteConfirmIds.length} draft invoice
          {batchDeleteConfirmIds.length === 1 ? "" : "s"} and associated
          line items will be permanently removed. This cannot be undone.
        </p>
        <div style={{ display: "flex", gap: "10px", marginTop: "18px" }}>
          <button
            type="button"
            className="nx-btn"
            style={{ flex: 1 }}
            onClick={() => setBatchDeleteConfirmIds(null)}
            disabled={batchDeleting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="nx-btn"
            style={{
              flex: 1,
              borderColor: "rgba(224,107,90,0.45)",
              color: "#e06b5a",
            }}
            onClick={handleConfirmBatchDelete}
            disabled={batchDeleting}
          >
            {batchDeleting ? "Deleting…" : "Delete Selected"}
          </button>
        </div>
      </div>
    </div>
    </ViewportPortal>
  ) : null;

  if (isDesktop) {
    return (
      <InvoiceHubDesktop
        onBack={onBack}
        invoices={invoices}
        filtered={filteredInvoices}
        loading={loading}
        statusFilter={statusFilter}
        onStatusFilter={setStatusFilter}
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
        canCreate={canReviewInvoice}
        onCreate={beginCreateInvoice}
        onOpen={openInvoice}
        showAccountsReceivable={canViewAccountsReceivable}
        arMetrics={arMetrics}
        arLoading={arLoading}
        hubError={hubError}
        batch={
          batchSelectionEnabled
            ? {
                enabled: true,
                selection: batchSelection,
                actions: batchMode.actions,
                busy: batchDeleting,
                onAction: handleBatchAction,
              }
            : null
        }
      >
        {batchDeleteModal}
      </InvoiceHubDesktop>
    );
  }

  return (
    <CrewPilotModuleShell
      title="Invoice Hub"
      subtitle="Invoices for your organization"
      sectionLabel="INVOICES"
      onBack={onBack}
      desktopPage
      primary
      compact
    >
      <div className="invoice-hub">
        {canReviewInvoice ? (
          <div className="hub-primary-slot">
            <HubPrimaryAction
              onClick={beginCreateInvoice}
              label="New Invoice"
              ariaLabel="+ New Invoice"
            />
          </div>
        ) : null}
        <InvoiceHubDashboard
          invoices={invoices}
          loading={loading}
          onSelectStatus={setStatusFilter}
          onOpenInvoice={openInvoice}
          showAccountsReceivable={canViewAccountsReceivable}
          arMetrics={arMetrics}
          arLoading={arLoading}
        />

        <div className="invoice-hub__toolbar">
          <input
            type="search"
            placeholder="Search invoices…"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            style={SEARCH_INPUT_STYLE}
          />

          <div
            className="invoice-hub__filters"
            style={{ ...STATUS_PILLS_WRAP_STYLE, marginTop: "14px" }}
          >
            <button
              type="button"
              aria-pressed={statusFilter === null}
              style={{
                ...STATUS_PILL_BTN_BASE_STYLE,
                ...(statusFilter === null ? STATUS_PILL_ACTIVE_GOLD_STYLE : {}),
              }}
              onClick={() => setStatusFilter(null)}
            >
              All
            </button>
            {INVOICE_HUB_FILTER_CHIPS.map((option) => (
              <button
                key={option.value}
                type="button"
                aria-pressed={statusFilter === option.value}
                style={{
                  ...STATUS_PILL_BTN_BASE_STYLE,
                  ...(statusFilter === option.value
                    ? STATUS_PILL_ACTIVE_GOLD_STYLE
                    : {}),
                }}
                onClick={() => setStatusFilter(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {hubError && (
          <div style={{ color: "#e06b5a", marginBottom: "12px" }}>
            {hubError}
          </div>
        )}

        {loading ? (
          <p style={{ opacity: 0.7 }}>Loading invoices…</p>
        ) : filteredInvoices.length === 0 ? (
          <p className="kpi-empty kpi-empty--pad">No invoices found.</p>
        ) : (
          <div className="invoice-hub__list">
            {batchSelectionEnabled && (
              <BatchSelectionToolbar
                allSelected={batchSelection.allSelected}
                someSelected={batchSelection.someSelected}
                onToggleAll={batchSelection.toggleAll}
                selectedCount={batchSelection.selectedCount}
                actions={batchMode.actions}
                onAction={handleBatchAction}
                disabled={batchDeleting}
              />
            )}

            {filteredInvoices.map((invoice) => (
              <InvoiceListCard
                key={invoice.invoice_id}
                invoice={invoice}
                onOpen={openInvoice}
                selectionEnabled={batchSelectionEnabled}
                selected={batchSelection.isSelected(invoice.invoice_id)}
                onToggleSelect={() =>
                  batchSelection.toggleItem(invoice.invoice_id)
                }
              />
            ))}
          </div>
        )}

        {batchDeleteModal}
      </div>
    </CrewPilotModuleShell>
  );
}
